import {
  getEstimatedTasksFromCredits,
  hasCompletedOnboarding,
  isClientOnALaCartePlan,
  type MembershipPlanShape,
} from '@frontend/api-client';
import type { Client, ClientStatus } from '@frontend/api-types';
import { PRICING_MODE_CONFIGS, type PricingMode } from '@frontend/react';
import { useMemo } from 'react';
import { type SubscriptionTerminologyConfig } from './subscriptionTerminologySchema';

type UseTerminologyOptions = {
  client?: Client;
  clientStatus?: ClientStatus;
  forceCredits?: boolean;
};

type UseTerminologyResult = {
  mode: PricingMode;
  pricingTerminology: SubscriptionTerminologyConfig;
  showCredits: boolean;
};

export const useTerminology = ({
  client,
  clientStatus,
  forceCredits = false,
}: UseTerminologyOptions): UseTerminologyResult => {
  const pricingMode: PricingMode = 'credit';

  const isALaCarte = isClientOnALaCartePlan(client);
  const isOnboardingComplete = hasCompletedOnboarding(clientStatus);
  const isCarveOut = (isALaCarte && isOnboardingComplete) || forceCredits;

  return useMemo(() => {
    const effectiveMode: PricingMode = isCarveOut ? 'credit' : pricingMode;
    const config = PRICING_MODE_CONFIGS[effectiveMode];

    return {
      mode: effectiveMode,
      pricingTerminology: config,
      showCredits: !config.hide_credits,
    };
  }, [pricingMode, isCarveOut]);
};

/**
 * Get plan subtitle using the config template.
 * Supports placeholders: {credits}, {min}, {max}
 */
export const getPlanSubtitle = (
  plan: Pick<MembershipPlanShape, 'creditsPerMonth' | 'estimatedTasksPerMonth'>,
  config: SubscriptionTerminologyConfig,
): string => {
  return config.plan_subtitle_template
    .replace('{credits}', String(plan.creditsPerMonth ?? 0))
    .replace('{min}', String(plan.estimatedTasksPerMonth.min))
    .replace('{max}', String(plan.estimatedTasksPerMonth.max));
};

/**
 * Get subtitle for a credit amount using the config template.
 * Useful for credit packages where we need to estimate tasks.
 * Supports placeholders: {credits}, {min}, {max}
 */
export const getCreditsSubtitle = (credits: number, config: SubscriptionTerminologyConfig): string => {
  const estimate = getEstimatedTasksFromCredits(credits);

  if (estimate.min === estimate.max) {
    return config.plan_subtitle_template
      .replace('{credits}', String(credits))
      .replace('~{min}-{max}', `~${estimate.min}`)
      .replace('{min}-{max}', String(estimate.min))
      .replace('{min}', String(estimate.min))
      .replace('{max}', String(estimate.max));
  }

  return config.plan_subtitle_template
    .replace('{credits}', String(credits))
    .replace('{min}', String(estimate.min))
    .replace('{max}', String(estimate.max));
};
